import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { ArrowRight, Trophy, Users, Shield, Zap, Target, Crown, CheckCircle, Smartphone, Gamepad2, Monitor, Swords, Share2, Play, Pause, RefreshCw, Sparkles, Sliders } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { DEMO_ESPORTS_ID } from '../../data/demoData'

export const EsportsLandingPage = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { t } = useTranslation()

    useEffect(() => {
        document.title = "ProPulse Esports | Professionele OBS Overlays & E-sports Toernooiplanner"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) {
            metaDesc.setAttribute("content", "Verander je e-sports toernooien in een liveshow met ProPulse. Koppel toernooien met OBS Studio via realtime Supabase overlays. Twitch & YouTube ready.")
        }
    }, [])

    // Simulator State
    const [simTime, setSimTime] = useState(74)
    const [simHomeScore, setSimHomeScore] = useState(2)
    const [simAwayScore, setSimAwayScore] = useState(1)
    const [isPlaying, setIsPlaying] = useState(true)
    const [showGoalFlash, setShowGoalFlash] = useState(false)
    const [flashText, setFlashText] = useState('')
    const [latency, setLatency] = useState(12)
    const [logs, setLogs] = useState([
        { time: '11:31:01', type: 'system', message: '🔌 WebSocket broadcast pipeline initialized' },
        { time: '11:31:02', type: 'info', message: '📡 Caster channel auth token validated successfully' },
        { time: '11:31:03', type: 'match', message: '⚔️ Match active: G2 Esports [1] - [2] FaZe Clan' }
    ])

    // Autoplay Interval
    useEffect(() => {
        if (!isPlaying) return

        const interval = setInterval(() => {
            setSimTime(prev => {
                const nextTime = prev + 1

                if (nextTime >= 90) {
                    // Reset Sim
                    setSimHomeScore(2)
                    setSimAwayScore(1)
                    setLogs([
                        { time: getTimestamp(), type: 'system', message: '🔄 Simulation restarted. Ready for testing.' },
                        { time: getTimestamp(), type: 'match', message: '⚔️ Match restarted: FaZe Clan vs G2 Esports' }
                    ])
                    return 74
                }

                // Random Goal/Action Events
                if (nextTime === 80) {
                    handleGoal('away', 'G2 Esports')
                } else if (nextTime === 88) {
                    handleGoal('home', 'FaZe Clan')
                } else if (nextTime % 3 === 0) {
                    // Generic action log
                    const actions = [
                        ' G2 Esports shoots, hits the crossbar! Spectacular save!',
                        '⏱️ Match intensity rising. FaZe Clan sets up a tight defense.',
                        ' G2 tactical pause requested. Lineups updated.',
                        '⚔️ High pressure in the G2 Esports penalty area!'
                    ]
                    const randomAction = actions[Math.floor(Math.random() * actions.length)]
                    addLog('info', randomAction)
                }

                return nextTime
            })
        }, 4000)

        return () => clearInterval(interval)
    }, [isPlaying, simTime])

    const getTimestamp = () => {
        const d = new Date()
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
    }

    const addLog = (type, message) => {
        setLogs(prev => [
            { time: getTimestamp(), type, message },
            ...prev.slice(0, 8)
        ])
    }

    const handleGoal = (side, teamName) => {
        // Increment Score
        if (side === 'home') {
            setSimHomeScore(s => s + 1)
        } else {
            setSimAwayScore(s => s + 1)
        }

        // Trigger flash overlay
        setFlashText(`GOAL! ${teamName.toUpperCase()} SCORES!`)
        setShowGoalFlash(true)
        setLatency(Math.floor(Math.random() * 5) + 6) // Latency drops to 6-10ms

        addLog('goal', `⚽ GOAL! ${teamName} scores a brilliant goal!`)

        setTimeout(() => {
            setShowGoalFlash(false)
        }, 2200)
    }

    // Manual triggers
    const triggerManualGoal = (side) => {
        const teamName = side === 'home' ? 'FaZe Clan' : 'G2 Esports'
        handleGoal(side, teamName)
    }

    const resetSimulation = () => {
        setSimTime(74)
        setSimHomeScore(2)
        setSimAwayScore(1)
        setShowGoalFlash(false)
        setLogs([
            { time: getTimestamp(), type: 'system', message: '🔄 Manual simulation reset executed' },
            { time: getTimestamp(), type: 'match', message: '⚔️ Ready for live simulation' }
        ])
    }

    const handleStart = () => {
        if (user) {
            navigate('/admin/dashboard')
        } else {
            navigate('/login')
        }
    }

    return (
        <div className="min-h-screen font-sans text-white bg-[#030306] selection:bg-purple-500 selection:text-white overflow-x-hidden relative">
            
            {/* Ambient Background Glows */}
            <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-600/10 blur-[150px] rounded-full pointer-events-none"></div>
            <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] bg-cyan-500/10 blur-[150px] rounded-full pointer-events-none"></div>
            <div className="absolute bottom-[10%] left-[20%] w-[700px] h-[700px] bg-indigo-600/10 blur-[180px] rounded-full pointer-events-none"></div>

            {/* Cyber Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

            {/* 0. NAVIGATION HEADER */}
            <nav className="absolute top-0 w-full z-50 px-6 py-4 md:py-6">
                <div className="container mx-auto flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                        <img
                            src="/images/logo.svg"
                            alt="ProPoule Logo"
                            className="w-8 h-8 md:w-10 md:h-10 transform group-hover:rotate-12 transition-transform filter drop-shadow-[0_0_8px_rgba(217,70,239,0.5)]"
                        />
                        <span className="font-black text-2xl md:text-3xl tracking-tighter uppercase italic select-none">
                            <span className="text-white">PROPOULE</span>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 ml-[0.15rem] font-light">ESPORTS</span>
                        </span>
                    </div>

                    {/* Central Brand Navigator Pill */}
                    <div className="hidden md:flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-6 py-2 backdrop-blur-lg">
                        <button onClick={() => navigate('/')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">⚽ ProPoule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/wkpoule')} className="text-xs font-bold text-slate-300 hover:text-orange-400 transition-colors uppercase tracking-widest">🏆 WK Poule</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/e-sports')} className="text-xs font-bold text-purple-400 uppercase tracking-widest">🎮 ProPoule Esports</button>
                        <span className="text-white/20">|</span>
                        <button onClick={() => navigate('/proracket')} className="text-xs font-bold text-slate-300 hover:text-green-400 transition-colors uppercase tracking-widest">🎾 ProPoule Racket</button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="text-gray-300 hover:text-white font-medium transition-colors hidden md:block"
                        >
                            {t('landing.nav_login')}
                        </button>
                        <Button
                            onClick={handleStart}
                            className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white rounded-xl px-6 border-0 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
                        >
                            {t('landing.nav_start')}
                        </Button>
                    </div>
                </div>
            </nav>

            {/* 1. HERO SECTION */}
            <section className="relative min-h-screen flex items-center justify-center pt-20">
                <div className="container mx-auto px-6 relative z-20 text-center">
                    
                    {/* Glowing Tech Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 backdrop-blur-md border border-purple-500/30 text-xs font-mono tracking-widest text-purple-300 mb-6 uppercase shadow-[0_0_15px_rgba(168,85,247,0.15)] animate-fade-in">
                        <Zap size={12} className="text-cyan-400 animate-pulse" />
                        Supabase Realtime Broadcast & OBS Integration
                    </div>

                    {/* Main Title */}
                    <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-6 leading-tight">
                        THE NEXT STANDARD FOR <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-fuchsia-500 to-cyan-400 drop-shadow-[0_0_20px_rgba(217,70,239,0.4)]">
                            LIVE STREAM OVERLAYS
                        </span> <br />
                        & ESPORTS TOURNAMENTS
                    </h1>

                    <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                        Verander je e-sports toernooien in een professionele liveshow. Koppel toernooien direct met OBS Studio via realtime Supabase Broadcasts met minder dan 50ms latency. Volledig geautomatiseerde brackets en overlays voor Twitch en YouTube creators.
                    </p>

                    {/* Action Grid */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-4xl mx-auto flex-wrap mb-16">
                        
                        <Button
                            onClick={handleStart}
                            className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white px-8 py-6 text-lg rounded-2xl shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all transform hover:-translate-y-1 border-0 min-w-[220px] font-black"
                        >
                            START GRATIS LEAGUE
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>

                        <button
                            onClick={() => navigate(`/live/${DEMO_ESPORTS_ID}`)}
                            className="group relative px-6 py-6 bg-cyan-950/20 hover:bg-cyan-900/40 backdrop-blur-md rounded-2xl font-bold text-white border border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.15)] overflow-hidden transition-all hover:scale-105 active:scale-95 min-w-[220px] text-left"
                        >
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-cyan-500/20 rounded-lg text-cyan-400 group-hover:text-white group-hover:bg-cyan-500 transition-colors shadow-[0_0_10px_rgba(6,182,212,0.3)]">
                                    <Gamepad2 size={20} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-cyan-400 uppercase tracking-widest font-mono">Live Demo</span>
                                    <span className="text-sm font-black tracking-tight">BEKIJK E-SPORTS LIVE</span>
                                </div>
                            </div>
                        </button>
                    </div>

                    {/* 🎮 INTERACTIVE LIVE SCORE & BROADCAST SIMULATOR */}
                    <div className="mt-12 relative mx-auto max-w-5xl">
                        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-cyan-400 rounded-3xl blur opacity-30 shadow-[0_0_60px_rgba(168,85,247,0.5)]"></div>
                        <div className="relative rounded-3xl border border-purple-500/20 overflow-hidden shadow-2xl bg-[#09090f]/95 backdrop-blur-lg p-6">
                            
                            {/* Widget Header Banner */}
                            <div className="flex flex-col md:flex-row justify-between items-center pb-4 mb-6 border-b border-purple-500/10 gap-4">
                                <div className="flex items-center gap-3 text-left">
                                    <div className="p-2 bg-purple-500/20 rounded-xl border border-purple-500/30 text-purple-300">
                                        <Sparkles size={18} className="animate-spin-slow" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm tracking-widest text-cyan-400 uppercase font-mono">Interactive Live Pitch Demo</h3>
                                        <p className="text-xs text-slate-400">Ervaar de minder-dan-50ms latency realtime sync simulatie!</p>
                                    </div>
                                </div>
                                
                                {/* Simulator Controls */}
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => setIsPlaying(!isPlaying)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 border transition-all ${
                                            isPlaying 
                                                ? 'bg-purple-950/40 text-purple-300 border-purple-500/30' 
                                                : 'bg-green-950/40 text-green-300 border-green-500/30 hover:bg-green-900/40'
                                        }`}
                                    >
                                        {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                                        {isPlaying ? 'Pauzeer Autoplay' : 'Start Autoplay'}
                                    </button>
                                    <button 
                                        onClick={resetSimulation}
                                        className="p-2 bg-slate-900 hover:bg-slate-800 border border-white/5 rounded-lg text-slate-300 transition-colors"
                                        title="Reset Simulatie"
                                    >
                                        <RefreshCw size={12} />
                                    </button>
                                </div>
                            </div>

                            {/* Main Simulator Content: Two columns (Stream vs Remote Admin Controller) */}
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                                
                                {/* COLUMN A: OBS Broadcast Screen Overlay View */}
                                <div className="lg:col-span-8 flex flex-col justify-between rounded-2xl border border-white/5 bg-[#030306] overflow-hidden relative min-h-[360px] shadow-inner">
                                    
                                    {/* Scoreboard HUD Overlay */}
                                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center bg-[#07070d]/90 border border-purple-500/25 px-5 py-2.5 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.2)] select-none backdrop-blur-md">
                                        <div className="flex items-center gap-3">
                                            <span className="text-white font-mono font-black text-xs uppercase tracking-wider">FaZe</span>
                                            <img src="/images/faze_clan.svg" className="w-5 h-5 object-contain" alt="" />
                                        </div>
                                        <div className="mx-4 flex items-center gap-1.5">
                                            <span className="text-cyan-400 font-mono font-black text-lg w-6 text-center drop-shadow-[0_0_6px_rgba(6,182,212,0.6)] animate-pulse">{simHomeScore}</span>
                                            <span className="text-slate-500 text-xs font-mono">-</span>
                                            <span className="text-cyan-400 font-mono font-black text-lg w-6 text-center drop-shadow-[0_0_6px_rgba(6,182,212,0.6)] animate-pulse">{simAwayScore}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <img src="/images/g2_esports.svg" className="w-5 h-5 object-contain" alt="" />
                                            <span className="text-white font-mono font-black text-xs uppercase tracking-wider">G2</span>
                                        </div>
                                        <div className="ml-4 pl-4 border-l border-white/10 flex items-center gap-1.5 font-mono text-[10px] text-purple-300 font-bold bg-purple-950/30 px-2 py-0.5 rounded border border-purple-500/10">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></span>
                                            {simTime}'
                                        </div>
                                    </div>

                                    {/* Mock Gameplay Frame Background */}
                                    <div className="w-full h-full relative min-h-[300px]">
                                        <img 
                                            src="/images/Esports_FC26.webp" 
                                            alt="Gameplay Overlay mockup" 
                                            className="absolute inset-0 w-full h-full object-cover opacity-35"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none"></div>

                                        {/* Goal Flash Announcement Overlay */}
                                        {showGoalFlash && (
                                            <div className="absolute inset-0 bg-purple-900/20 backdrop-blur-[2px] z-20 flex items-center justify-center animate-fade-in">
                                                <div className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-black text-2xl md:text-3xl px-8 py-4 rounded-2xl shadow-[0_0_40px_rgba(168,85,247,0.8)] border border-purple-300/20 animate-bounce tracking-tighter">
                                                    {flashText}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Realtime Stream Info Bottom Bar */}
                                    <div className="bg-black/80 border-t border-white/5 p-4 flex flex-col sm:flex-row justify-between items-center gap-2 relative z-15">
                                        <div className="flex items-center gap-2">
                                            <Monitor size={14} className="text-cyan-400" />
                                            <span className="text-[11px] font-mono tracking-widest text-slate-300">OBS STREAM BROADCAST OVERLAY</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span className="text-[10px] font-mono border border-purple-500/20 px-2.5 py-1 rounded bg-purple-950/20 text-purple-300 flex items-center gap-1">
                                                <Zap size={10} className="text-cyan-400" />
                                                BROADCAST SYNC: <strong className="text-cyan-300">{latency}ms</strong>
                                            </span>
                                            <span className="text-[10px] font-mono border border-cyan-500/20 px-2.5 py-1 rounded bg-cyan-950/20 text-cyan-300">vMix & OBS RESOLUTION: 1080p</span>
                                        </div>
                                    </div>
                                </div>

                                {/* COLUMN B: Caster Admin Remote Controller Widget */}
                                <div className="lg:col-span-4 flex flex-col justify-between gap-4">
                                    
                                    {/* Mock Smartphone Box */}
                                    <div className="rounded-2xl border border-purple-500/10 bg-[#0c0c14] p-4 flex-1 flex flex-col justify-between text-left shadow-lg">
                                        <div className="flex justify-between items-center border-b border-purple-500/10 pb-2 mb-3">
                                            <div className="flex items-center gap-1.5">
                                                <Smartphone size={14} className="text-purple-400" />
                                                <span className="text-[10px] font-mono tracking-widest text-purple-300 uppercase font-black">Caster Remote (Admin)</span>
                                            </div>
                                            <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-green-950 border border-green-500/20 text-green-400 uppercase font-bold animate-pulse">Connected</span>
                                        </div>

                                        {/* Phone Interface */}
                                        <div className="bg-[#07070b] border border-white/5 rounded-xl p-3 space-y-3 flex-1 flex flex-col justify-between">
                                            <div className="text-center">
                                                <span className="text-[8px] font-mono text-slate-400 uppercase tracking-widest block mb-1">Live Score Controller</span>
                                                <h4 className="text-xs font-black text-white leading-none">E-Sports Cup Final 🏆</h4>
                                            </div>

                                            {/* Score controllers */}
                                            <div className="grid grid-cols-2 gap-2 my-2">
                                                <div className="bg-purple-950/20 border border-purple-500/10 p-2.5 rounded-lg text-center flex flex-col items-center">
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase">FaZe Score</span>
                                                    <span className="text-2xl font-black text-white font-mono my-1">{simHomeScore}</span>
                                                    <div className="flex gap-1.5 mt-1">
                                                        <button 
                                                            onClick={() => triggerManualGoal('home')}
                                                            className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded text-xs font-black transition-all shadow-[0_0_8px_rgba(168,85,247,0.3)] active:scale-95"
                                                        >
                                                            +1
                                                        </button>
                                                        <button 
                                                            onClick={() => setSimHomeScore(s => Math.max(0, s - 1))}
                                                            className="px-2 py-1 bg-slate-900 border border-white/5 hover:bg-slate-800 text-slate-400 rounded text-xs font-bold"
                                                        >
                                                            -
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="bg-cyan-950/20 border border-cyan-500/10 p-2.5 rounded-lg text-center flex flex-col items-center">
                                                    <span className="text-[9px] font-bold text-slate-400 uppercase">G2 Score</span>
                                                    <span className="text-2xl font-black text-white font-mono my-1">{simAwayScore}</span>
                                                    <div className="flex gap-1.5 mt-1">
                                                        <button 
                                                            onClick={() => triggerManualGoal('away')}
                                                            className="px-3 py-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-xs font-black transition-all shadow-[0_0_8px_rgba(6,182,212,0.3)] active:scale-95"
                                                        >
                                                            +1
                                                        </button>
                                                        <button 
                                                            onClick={() => setSimAwayScore(s => Math.max(0, s - 1))}
                                                            className="px-2 py-1 bg-slate-900 border border-white/5 hover:bg-slate-800 text-slate-400 rounded text-xs font-bold"
                                                        >
                                                            -
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Pitch Info Tip */}
                                            <div className="text-[9px] text-slate-400 border border-purple-500/10 rounded p-2 bg-purple-950/10 flex items-start gap-1">
                                                <Zap size={10} className="text-cyan-400 shrink-0 mt-0.5" />
                                                <span>Klik op <strong>+1</strong> om een real-time goal te simuleren en zie het linker streamscherm direct updaten!</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Simulated Developer Pipeline Logs Terminal */}
                                    <div className="rounded-2xl border border-white/5 bg-[#040409] p-3 text-left font-mono h-[140px] flex flex-col">
                                        <div className="flex items-center justify-between border-b border-white/5 pb-1 mb-2 text-[8px] tracking-wider text-slate-500 uppercase font-black">
                                            <span>Broadcast Pipeline Logs</span>
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping"></span>
                                        </div>
                                        <div className="flex-1 overflow-y-auto text-[9px] space-y-1 scrollbar-hide">
                                            {logs.map((log, idx) => (
                                                <div key={idx} className="flex gap-1 items-start leading-relaxed truncate">
                                                    <span className="text-slate-600 shrink-0 select-none">[{log.time}]</span>
                                                    <span className={
                                                        log.type === 'goal' ? 'text-green-400 font-bold' :
                                                        log.type === 'system' ? 'text-cyan-400' :
                                                        log.type === 'match' ? 'text-purple-300' : 'text-slate-400'
                                                    }>{log.message}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. THE MOAT: CORE INNOVATIVE FEATURES */}
            <section className="py-32 relative z-20">
                <div className="container mx-auto px-6">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <h2 className="text-cyan-400 font-mono tracking-widest uppercase mb-4 text-xs font-bold">The E-sports Moat</h2>
                        <h3 className="text-4xl md:text-5xl font-black text-white leading-tight">
                            DRIE UNIEKE FEATURES DIE JOUW STREAM EEN ENTERPRISE LOOK GEVEN
                        </h3>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Feature A */}
                        <div className="glass-strong premium-card rounded-2xl p-8 border border-white/5 hover:border-purple-500/30 transition-all hover:scale-[1.02] flex flex-col justify-between h-[360px] relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 rounded-full blur-2xl group-hover:bg-purple-500/10 transition-colors"></div>
                            <div>
                                <div className="p-3 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-xl w-fit mb-6 shadow-[0_0_15px_rgba(168,85,247,0.2)]">
                                    <Swords size={24} />
                                </div>
                                <h4 className="text-xl font-bold mb-3 tracking-wide">De "Ghost Admin" Ref-Modus</h4>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Nooit meer alt-tabben om scores bij te werken. Een co-caster of externe scheidsrechter kan vanaf zijn eigen dashboard de stand live wijzigen. Je stream-overlay updates instant mee.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest font-bold">100% Gecentraliseerd</span>
                        </div>

                        {/* Feature B */}
                        <div className="glass-strong premium-card rounded-2xl p-8 border border-white/5 hover:border-cyan-500/30 transition-all hover:scale-[1.02] flex flex-col justify-between h-[360px] relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-full blur-2xl group-hover:bg-cyan-500/10 transition-colors"></div>
                            <div>
                                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-xl w-fit mb-6 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                                    <Target size={24} />
                                </div>
                                <h4 className="text-xl font-bold mb-3 tracking-wide">Context-Aware Sponsors</h4>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Zet sponsorlogo's slim in. Laat sponsor-uitingen programmatisch oplichten bij Match Points, Grand Finals of pauzes met classy, geanimeerde glassmorphism banners.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest font-bold">Programmatische Omzet</span>
                        </div>

                        {/* Feature C */}
                        <div className="glass-strong premium-card rounded-2xl p-8 border border-white/5 hover:border-fuchsia-500/30 transition-all hover:scale-[1.02] flex flex-col justify-between h-[360px] relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-fuchsia-500/5 rounded-full blur-2xl group-hover:bg-fuchsia-500/10 transition-colors"></div>
                            <div>
                                <div className="p-3 bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-400 rounded-xl w-fit mb-6 shadow-[0_0_15px_rgba(217,70,239,0.2)]">
                                    <Share2 size={24} />
                                </div>
                                <h4 className="text-xl font-bold mb-3 tracking-wide">De "Pro-Card" Player Passport</h4>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Creëer virale groei. Elke deelnemer krijgt automatisch een interactief "Pro-Card" paspoort. Spelers delen hun historische win-rates en badges op Twitter/Discord en trekken nieuwe leads aan.
                                </p>
                            </div>
                            <span className="text-[10px] font-mono text-fuchsia-400 uppercase tracking-widest font-bold">Product-Led Growth</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. ZERO-COST TRUST & CONVERSION MODEL */}
            <section className="py-24 bg-[#05050a] border-y border-white/5 relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    
                    <div className="max-w-4xl mx-auto">
                        <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">
                            BETROUWBARE INFRASTRUCTUUR VOOR PROFESSIONELE ORGANISATIES
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
                            {/* Trust Badge 1 */}
                            <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-5 text-left backdrop-blur-md">
                                <div className="p-3 bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30">
                                    <Shield size={24} />
                                </div>
                                <div>
                                    <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest font-bold block mb-0.5">PLATFORM SECURITY</span>
                                    <h4 className="font-black text-white text-sm">Infrastructure Secured by Clarity</h4>
                                    <p className="text-xs text-slate-400 mt-0.5">Realtime monitoring & DDoS protection actief.</p>
                                </div>
                            </div>

                            {/* Trust Badge 2 */}
                            <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-5 text-left backdrop-blur-md">
                                <div className="p-3 bg-purple-500/20 text-purple-400 rounded-xl border border-purple-500/30">
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <span className="text-xs font-mono text-purple-400 uppercase tracking-widest font-bold block mb-0.5">SECURE TRANSACTIONS</span>
                                    <h4 className="font-black text-white text-sm">Payments Powered by Stripe</h4>
                                    <p className="text-xs text-slate-400 mt-0.5">Veilige PCI-DSS compliant creditcard afhandeling.</p>
                                </div>
                            </div>
                        </div>

                        <p className="text-xs text-slate-500 font-mono">
                            CLARITY SECURITY HUB ID: #SH-2026-PPV2 • DEPLOYED REGION: EU-WEST
                        </p>
                    </div>

                </div>
            </section>

            {/* 4. OBS WORKFLOW EXPLANATION SECTION */}
            <section className="py-32 relative">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center gap-16">
                        
                        <div className="flex-1 space-y-6 relative z-10 text-left">
                            <span className="text-purple-400 font-mono tracking-widest uppercase text-xs font-bold">Hoe werkt de stream overlay?</span>
                            <h2 className="text-4xl md:text-5xl font-black leading-tight">
                                LIVE SCOREBORD OP JE STREAM IN 3 SIMPELE STAPPEN
                            </h2>
                            <p className="text-slate-300 leading-relaxed">
                                Je hoeft geen programmeur te zijn om een professioneel scorebord op je stream te tonen. ProPoule levert een kant-en-klare link die direct met OBS, Streamlabs of vMix praat.
                            </p>

                            <div className="space-y-6 pt-4">
                                <div className="flex gap-4">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-600 font-mono font-bold text-white shadow-[0_0_10px_rgba(168,85,247,0.4)]">1</div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg">Maak je Toernooi aan</h4>
                                        <p className="text-slate-400 text-sm mt-0.5">Voer je teams of spelers in de e-sports wizard van ProPoule.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-600 font-mono font-bold text-white shadow-[0_0_10px_rgba(168,85,247,0.4)]">2</div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg">Kopieer de Browserbron Link</h4>
                                        <p className="text-slate-400 text-sm mt-0.5">Kopieer de unieke, beveiligde overlay link uit de toernooi instellingen.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-600 font-mono font-bold text-white shadow-[0_0_10px_rgba(168,85,247,0.4)]">3</div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg">Plak in OBS & Stream live!</h4>
                                        <p className="text-slate-400 text-sm mt-0.5">Voeg een 'Browserbron' toe in OBS, plak de link, en geniet van automatische score-updates.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 relative z-10">
                            <div className="relative mx-auto w-full max-w-md bg-[#0a0a0f] border border-white/10 rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-6">
                                    <div className="flex items-center gap-2">
                                        <Monitor size={18} className="text-cyan-400" />
                                        <span className="font-bold text-sm tracking-wide">OBS Studio Source settings</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                                        <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="text-left">
                                        <label className="text-xs font-mono text-slate-400 block mb-1.5 uppercase">Source Name</label>
                                        <div className="bg-[#121217] border border-white/5 p-3 rounded-lg text-sm text-white font-mono">ProPoule Scoreboard HUD</div>
                                    </div>
                                    <div className="text-left">
                                        <label className="text-xs font-mono text-slate-400 block mb-1.5 uppercase">URL (Overlay Link)</label>
                                        <div className="bg-[#121217] border border-cyan-500/30 p-3 rounded-lg text-xs text-cyan-300 font-mono overflow-x-auto shadow-inner">
                                            https://propoule.com/overlay/{DEMO_ESPORTS_ID}?key=pr_f78a9c...
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-left">
                                        <div>
                                            <label className="text-xs font-mono text-slate-400 block mb-1.5 uppercase">Width (px)</label>
                                            <div className="bg-[#121217] border border-white/5 p-3 rounded-lg text-sm text-white font-mono">1920</div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-mono text-slate-400 block mb-1.5 uppercase">Height (px)</label>
                                            <div className="bg-[#121217] border border-white/5 p-3 rounded-lg text-sm text-white font-mono">1080</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* 5. PRICING SECTION */}
            <section className="py-32 relative bg-[#07070c]" id="pricing">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl text-white font-black mb-4 uppercase tracking-wide">E-SPORTS PACKAGES</h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full"></div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
                        {/* Free Tier */}
                        <div className="bg-[#101015] p-10 rounded-3xl border border-white/5 hover:border-white/10 transition-colors h-full flex flex-col text-left">
                            <h3 className="text-xl font-bold text-white mb-2">Starter Gamer</h3>
                            <div className="flex items-baseline gap-1 mb-6">
                                <span className="text-4xl font-black text-white">€0</span>
                                <span className="text-slate-500">/ forever free</span>
                            </div>
                            <p className="text-slate-400 mb-8 pb-8 border-b border-white/5">
                                Perfect voor incidentele community toernooien of LAN-party's met je vrienden.
                            </p>
                            <ul className="space-y-4 mb-10 flex-1">
                                {['1 Actieve League', 'Max 12 Spelers / Teams', 'Basis Speelschema generator', 'Live Standen op mobiel'].map(f => (
                                    <li key={f} className="flex items-center gap-3 text-slate-300 text-sm">
                                        <CheckCircle size={18} className="text-purple-500 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={handleStart}
                                className="w-full py-6 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10"
                            >
                                Start Starter League
                            </Button>
                        </div>

                        {/* PRO - Streamer Tier */}
                        <div className="relative transform md:scale-105 z-10 text-left">
                            <div className="absolute -inset-1.5 bg-gradient-to-br from-purple-600 to-cyan-400 rounded-3xl blur opacity-75 shadow-[0_0_30px_rgba(168,85,247,0.3)]"></div>
                            <div className="relative bg-[#13131b] p-10 rounded-3xl border border-purple-500/20 h-full flex flex-col">
                                <div className="absolute top-0 right-10 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-bold px-4 py-1 rounded-full text-xs uppercase tracking-wider shadow-lg">
                                    MOST POPULAR
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">Stream Pro</h3>
                                <div className="flex items-baseline gap-1 mb-6">
                                    <span className="text-5xl font-black text-white">€9,95</span>
                                    <span className="text-slate-400">/ maand</span>
                                </div>
                                <p className="text-slate-400 mb-8 pb-8 border-b border-purple-500/10">
                                    De ultieme distributiemotor voor e-sports streamers, league organizers & communities.
                                </p>
                                <ul className="space-y-4 mb-10 flex-1">
                                    {['Onbeperkt E-sports Leagues', 'Onbeperkt Spelers & Teams', 'Realtime OBS Studio Overlay 🎥', 'Ghost Admin & Co-caster Modus', 'Context-Aware Sponsor Triggers', 'Individuele Pro-Card Paspoorten', 'Geen Reclame'].map(f => (
                                        <li key={f} className="flex items-center gap-3 text-slate-200 text-sm font-semibold">
                                            <CheckCircle size={18} className="text-cyan-400 shrink-0 shadow-[0_0_10px_rgba(6,182,212,0.3)]" /> {f}
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    onClick={handleStart}
                                    className="w-full py-6 text-lg font-black bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white border-0 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.4)]"
                                >
                                    Start 7 Dagen Pro-Trial
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. FAQ & SEO SECTION */}
            <section className="py-24 bg-[#030306] border-t border-white/5">
                <div className="container mx-auto px-6 max-w-4xl text-left">
                    
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-6">E-Sports Overlay & Platform FAQ</h2>
                        <p className="text-slate-400 leading-relaxed">
                            Alles wat je moet weten over de geavanceerde streaming-overlays en e-sports toernooi planner features van ProPoule.
                        </p>
                    </div>

                    <div className="space-y-4 mb-20">
                        {[
                            {
                                q: "Werkt de stream-overlay met alle streaming software?",
                                a: "Ja! Onze overlays genereren een universele, responsive URL. Je plakt hem simpelweg als 'Browserbron' in OBS Studio, Streamlabs, vMix, XSplit of Twitch Studio. Het werkt direct client-side zonder extra plugins."
                            },
                            {
                                q: "Hoeveel vertraging zit er op de realtime updates?",
                                a: "Nagenoeg geen! Omdat we Supabase Realtime Broadcast kanalen gebruiken in plaats van database polling, is de latency onder de 50ms. De overlay past zich direct aan zodra de admin of referee de stand invoert."
                            },
                            {
                                q: "Wat is de Ghost Admin Ref-modus?",
                                a: "Streamers hoeven tijdens het gamen hun game niet te minimaliseren. Met de Ghost Admin modus geef je een moderator of caster een speciale scheidsrechters-PIN. Zij passen de stand aan via hun telefoon of tablet en de stream toont direct de nieuwe score."
                            },
                            {
                                q: "Kan ik sponsors toevoegen aan de live streams?",
                                a: "Zeker! In het Stream Pro pakket kun je sponsoren invoeren. De overlay is 'context-aware', wat betekent dat sponsors dynamisch in beeld verschijnen bij specifieke toernooi events (zoals Match Point, Finales of in de poule-fase)."
                            }
                        ].map((faq, i) => (
                            <details key={i} className="group bg-[#09090f] border border-white/10 rounded-xl p-6 [&_summary::-webkit-details-marker]:hidden cursor-pointer hover:border-purple-500/30 transition-colors">
                                <summary className="flex items-center justify-between text-lg font-bold text-white group-open:text-purple-400 transition-colors">
                                    <span>{faq.q}</span>
                                    <span className="text-2xl text-slate-500 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <div className="mt-4 text-slate-400 leading-relaxed text-sm border-t border-white/5 pt-4" dangerouslySetInnerHTML={{ __html: faq.a }} />
                            </details>
                        ))}
                    </div>

                    {/* Hidden SEO Text Cluster */}
                    <div className="bg-[#08080c] border border-white/5 rounded-2xl p-8 text-[11px] text-slate-500 leading-relaxed text-center font-mono">
                        <h3 className="font-bold uppercase text-slate-400 mb-2 tracking-wider">Esports SEO Target Keywords</h3>
                        <p>
                            Esports tournament bracket generator • Twitch live scoreboard overlay • OBS stream graphics package • automated brackets for esports • FIFA tournament planner online • CS2 bracket maker • valorant league manager app • discord bot tournament integration • live standings stream panel • custom gaming leaderboard software • propulse • propoule • proracket • pro-pulse • pro-racket • e-sports overlays nederland.
                        </p>
                        <p className="mt-4 font-sans text-xs text-slate-400 leading-relaxed">
                            ProPulse is hét professionele e-sports overlay platform van ProPoule en ProRacket. Plan automatische brackets, koppel live scores direct met Twitch en YouTube via OBS, en activeer de unieke Ghost Admin scheidsrechters-module voor naadloze stream-uitzendingen.
                        </p>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="py-12 border-t border-white/10 bg-black text-center text-slate-500">
                <p>&copy; 2026 ProPoule & Clarity Solutions. Alle rechten voorbehouden.</p>
                <div className="flex justify-center gap-6 mt-4 text-sm font-semibold">
                    <button onClick={() => navigate('/privacy')} className="hover:text-purple-400 transition-colors">Privacy Policy</button>
                    <button onClick={() => navigate('/terms')} className="hover:text-purple-400 transition-colors">Algemene Voorwaarden</button>
                </div>
            </footer>
        </div>
    )
}
